import React, { useState, useEffect } from "react";
import { Tree } from "primereact/tree";
import { useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { Card, CardBody } from "../../shared/components/Card";
import { FormItem } from "../../shared/components/form/FormElements";
import CheckboxInputComponent from "../../shared/components/form/CheckboxInputComponent";
import { useFormContext } from "react-hook-form";
import { VehicleImg } from "../../shared/components/table/components/VehicleComponent";
import { s3Url } from "../../shared/constants";
import { ActionIconContainer } from "../../shared/components/TableElements";
import { useTranslation } from "react-i18next";
import defaultVehicleIcon from "@/shared/img/defaultVehicleIcon.png";
import defaultEquipmentIcon from "@/shared/img/defaultEquipmentIcon.png";
import { formInputStyles } from "../../shared/constants/styles";
import { postApi } from "../../api/getUser";
import InfoPopup from "../../shared/components/InfoPopup";
import {
  colorTextNew,
  colorYellowNew,
  scrollbarStyles,
} from "../../utils/palette";
import styled from "styled-components";
import "./TreeStyles.css";

// Build tree structure
const buildTreeData = (groups, vehicles, filterText = "") => {
  const groupMap = new Map();

  groups.forEach((group) => {
    groupMap.set(group.group_id, {
      key: `group_${group.group_id}`,
      label: group.label,
      data: group.group_id,
      children: [],
    });
  });

  vehicles.forEach((vehicle) => {
    if (vehicle.group_id && groupMap.has(vehicle.group_id)) {
      groupMap.get(vehicle.group_id).children.push({
        key: `vehicle_${vehicle.veh_id}`,
        label: vehicle.label,
        data: vehicle.veh_id,
        type: "vehicle",
      });
    }
  });

  // Organize groups into a nested structure
  const treeData = [];
  groups.forEach((group) => {
    if (!group.parent_group || !groupMap.has(group.parent_group)) {
      treeData.push(groupMap.get(group.group_id));
    } else if (groupMap.has(group.parent_group)) {
      groupMap
        .get(group.parent_group)
        .children.push(groupMap.get(group.group_id));
    }
  });

  // Step 4: Add standalone vehicles that do not belong to any group
  const standaloneVehicles = vehicles
    .filter((vehicle) => !vehicle.group_id || !groupMap.has(vehicle.group_id))
    .map((vehicle) => ({
      key: `vehicle_${vehicle.veh_id}`,
      label: vehicle.label,
      selectable: true,
      type: "vehicle",
    }));

  if (standaloneVehicles.length > 0) {
    treeData.push(...standaloneVehicles);
  }

  //Filter logic
  if (filterText) {
    const filterNode = (node) => {
      if (node.label.toLowerCase().includes(filterText.toLowerCase())) {
        return true;
      }
      if (node.children) {
        return node.children.some(filterNode);
      }
      return false;
    };

    return treeData.filter(filterNode);
  }

  return treeData;
};

const VehiclesAssignedFormNew = ({
  groups,
  vehicles,
  selectedKeys,
  setSelectedKeys,
  vehiclesAssigned,
  setVehiclesAssigned,
  type,
  rowData,
  vehAssignedUpdated,
  setVehAssignedUpdated,
}) => {
  const vehiclesList = useSelector((state) => state.vehicles.list);

  const { orgId, roleId } = useSelector((state) => state.user);

  const { t } = useTranslation("common");

  const status = useFormContext().watch("status");
  const role = useFormContext().watch("role");

  //const [selectedKeys, setSelectedKeys] = useState({});
  const [filterValue, setFilterValue] = useState("");
  const [treeData, setTreeData] = useState(
    buildTreeData(groups, vehicles, filterValue)
  );
  //const [assignedVehicles, setAssignedVehicles] = useState([]);

  const [noUsersAssignedWarningMsg, setNoUsersAssignedWarningMsg] =
    useState("");

  // Check if filtering is active
  const isFiltering = filterValue.trim().length > 0;
  const allGroupsSelected = groups.every(
    (group) => selectedKeys[`group_${group.group_id}`]?.checked
  );
  const allVehiclesSelected = vehicles.every(
    (vehicle) => selectedKeys[`vehicle_${vehicle.veh_id}`]?.checked
  );

  const isSelectAllChecked = allGroupsSelected && allVehiclesSelected;

  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
  //   const isSelectAllChecked = useMemo(() => {
  //     if (!treeData.length) return false;

  //     let totalNodes = 0,
  //       selectedNodes = 0;

  //     treeData.forEach((node) => {
  //       collectCount(node);
  //     });

  //     function collectCount(node) {
  //       totalNodes++;
  //       if (selectedKeys[node.key]) selectedNodes++;

  //       if (node.children) {
  //         node.children.forEach(collectCount);
  //       }
  //     }

  //     return totalNodes > 0 && totalNodes === selectedNodes;
  //   }, [selectedKeys, treeData]);

  const [expandedKeys, setExpandedKeys] = useState({});

  //Effect to collapse all nodes when filtering
  useEffect(() => {
    if (filterValue) {
      expandAll();
    } else {
      setExpandedKeys({});
    }
  }, [filterValue]);

  useEffect(() => {
    setTreeData(buildTreeData(groups, vehicles, filterValue));
  }, [filterValue, groups, vehicles]);

  const expandAll = () => {
    let _expandedKeys = {};

    for (let node of treeData) {
      expandNode(node, _expandedKeys);
    }

    setExpandedKeys(_expandedKeys);
  };

  //Effect to update vehiclesAssigned on edit

  //use vehiclesList here instead of vehicles since vehicles changes on change of manager and causes rerender
  //of this effect. This effect is meant to run only once on edit
  useEffect(() => {
    if (type === "edit" && vehiclesList?.length > 0) {
      //const { user_id } = rowData;
      const user_id = rowData?.user_id;
      (async () => {
        let editKeys = {};
        if (rowData.group_ids) {
          const groupIds = JSON.parse(rowData.group_ids);
          if (groupIds?.length) {
            groupIds.forEach((groupId) => {
              editKeys[`group_${groupId}`] = { checked: true };
            });
          }
        }
        const res = await postApi(
          {
            user_id: user_id,
            querystring: "getoperatorvehicle",
          },
          "commonNew"
        );
        const { user_data } = res;
        if (user_data.length > 0) {
          const selectedVehIds = user_data
            .filter((v) => v.veh_active === 1)
            .map((v) => v.veh_id);

          const selectedVehicles = vehiclesList.filter((v) =>
            selectedVehIds.includes(v.veh_id)
          );
          setVehiclesAssigned(selectedVehicles);

          selectedVehIds.forEach((vehId) => {
            editKeys[`vehicle_${vehId}`] = { checked: true };
          });
        }
        setSelectedKeys(editKeys);
      })();
    }
  }, [rowData, vehiclesList]);

  const expandNode = (node, _expandedKeys) => {
    if (node.children && node.children.length) {
      // if (node.label.includes(globalFilter)) {
      //   _expandedKeys[node.key] = true;
      // }

      // if (node.parentGroup) {
      //   _expandedKeys[node.parentGroup] = true;
      // } else {
      //   _expandedKeys[node.key] = true;
      // }
      _expandedKeys[node.key] = true;

      for (let child of node.children) {
        expandNode(child, _expandedKeys);
      }
    }
  };

  const checkUserAssigned = async (vehicleId) => {
    const res = await postApi(
      {
        veh_id: vehicleId,
        querystring: "getsinglevehicle",
      },
      "commonNew"
    );
    const { user_data } = res;
    if (user_data.length <= 1) {
      const vehicleName = vehicles.find((v) => v.veh_id === vehicleId)?.name;
      setNoUsersAssignedWarningMsg({
        msg: t("single_veh_access_warn_msg", { vehicleName }),
      });
    }
  };

  const checkMultiVehiclesUserAssigned = async (vehiclesRemoved) => {
    let vehiclesWithNoUserAssigned = [];
    for (let v of vehiclesRemoved) {
      const res = await postApi(
        {
          veh_id: v.veh_id,
          querystring: "getsinglevehicle",
        },
        "commonNew"
      );
      const { user_data } = res;
      if (user_data.length <= 1) {
        vehiclesWithNoUserAssigned.push(v.name);
      }

      if (vehiclesWithNoUserAssigned.length > 0) {
        setNoUsersAssignedWarningMsg({
          msg: t("multi_veh_access_warn_msg"),
          listMsg: vehiclesWithNoUserAssigned,
        });
      }
    }
  };

  // 🔍 Find which node was clicked (compares previous and new selectedKeys)
  // const findClickedNodeKey = (prevSelectedKeys, newSelectedKeys) => {
  //   for (let key in newSelectedKeys) {
  //     if (!prevSelectedKeys[key]) return key; // New selection
  //   }
  //   for (let key in prevSelectedKeys) {
  //     if (!newSelectedKeys[key]) return key; // Deselection
  //   }
  //   return null; // No change detected
  // };

  // const removeParentNodes = (clickedNodeKey, treeData, selectedKeys) => {
  //   const parentKeys = findParentKeys(clickedNodeKey, treeData);

  //   console.log("parentKeys", parentKeys);

  //   // Remove all parent nodes from selectedKeys
  //   parentKeys.forEach((parentKey) => {
  //     console.log(
  //       groups?.find((g) => g.group_id === parentKey?.replace("group_", ""))
  //         ?.group_name,
  //       selectedKeys[parentKey]
  //     );

  //     let otherChildrenChecked = false;

  //     if (selectedKeys[parentKey]?.partialChecked) {
  //       selectedKeys[parentKey] = { checked: true, partialChecked: false };
  //     } else {
  //       delete selectedKeys[parentKey];
  //     }
  //   });
  // };

  // 🔎 Find all parent keys of a given node
  // const findParentKeys = (nodeKey, nodes, parentKey = null) => {
  //   for (let node of nodes) {
  //     if (node.key === nodeKey) {
  //       return parentKey ? [parentKey] : [];
  //     }
  //     if (node.children) {
  //       const parentKeys = findParentKeys(nodeKey, node.children, node.key);
  //       if (parentKeys.length) {
  //         return [node.key, ...parentKeys]; // Collect all ancestor keys
  //       }
  //     }
  //   }
  //   return [];
  // };
  // const findParentKeys = (nodeKey, nodes, parentKey = null, parents = []) => {
  //   for (let node of nodes) {
  //     if (node.key === nodeKey) {
  //       return parents;
  //     }
  //     if (node.children) {
  //       const result = findParentKeys(nodeKey, node.children, node.key, [
  //         ...parents,
  //         node.key,
  //       ]);
  //       if (result.length) return result;
  //     }
  //   }
  //   return [];
  // };

  // Handle selection change
  const handleSelectionChange = (e) => {
    if (!vehAssignedUpdated) {
      setVehAssignedUpdated(true);
    }

    const { value } = e;
    //let newKeys = { ...value };
    //Remove partialChecked property
    // if (!isObjEmpty(value)) {
    //   const clickedNodeKey = findClickedNodeKey(selectedKeys, value);
    //   console.log("clickedNodeKey", clickedNodeKey);
    //   for (let key in newKeys) {
    //     if (
    //       newKeys[key].hasOwnProperty("partialChecked") &&
    //       newKeys[key].partialChecked
    //     ) {
    //       delete newKeys[key];
    //       //newKeys[key] = { partialChecked: false, checked: true };
    //     }

    //     //console.log("newKeys", newKeys, key);

    //     // if (newKeys[key] && newKeys[key].checked && clickedNodeKey != key) {
    //     //   delete newKeys[key];
    //     // }
    //   }

    //   //removeParentNodes(clickedNodeKey, treeData, newKeys);
    // }
    setSelectedKeys(value);

    // Extract selected vehicle IDs
    const selectedVehicleIds = Object.keys(value)
      .filter((key) => key.startsWith("vehicle_"))
      .map((key) => key.replace("vehicle_", ""));

    if (roleId == 2) {
      let vehiclesUnchecked = [];
      vehiclesAssigned.forEach((v) => {
        if (!selectedVehicleIds.includes(v.veh_id)) {
          vehiclesUnchecked.push(v);
        }
      });
      if (vehiclesUnchecked.length) {
        if (vehiclesUnchecked.length === 1) {
          checkUserAssigned(vehiclesUnchecked[0].veh_id);
        } else {
          checkMultiVehiclesUserAssigned(vehiclesUnchecked);
        }
      }
    }

    // Update assigned vehicles
    const updatedAssignedVehicles = vehicles.filter((v) =>
      selectedVehicleIds.includes(v.veh_id)
    );
    setVehiclesAssigned(updatedAssignedVehicles);
  };

  // const handleSelectionChange = (e) => {
  //   let newSelectedKeys = { ...e.value };

  //   // Recursively unselect all child nodes if a parent is unchecked
  //   const unselectChildren = (node) => {
  //     if (node.children) {
  //       node.children.forEach((child) => {
  //         delete newSelectedKeys[child.key];
  //         unselectChildren(child);
  //       });
  //     }
  //   };

  //   // Check if any parent is being unchecked, remove all its children from selection
  //   Object.keys(selectedKeys).forEach((key) => {
  //     if (!newSelectedKeys[key]) {
  //       const nodeToUncheck = findNodeByKey(treeData, key);
  //       if (nodeToUncheck) {
  //         unselectChildren(nodeToUncheck);
  //       }
  //     }
  //   });

  //   // Prevent parents from being selected when only a child is checked
  //   Object.keys(newSelectedKeys).forEach((key) => {
  //     const node = findNodeByKey(treeData, key);
  //     if (node && node.children) {
  //       // If the node has children, remove it from selection (only allow leaf nodes)
  //       delete newSelectedKeys[key];
  //     }
  //   });

  //   setSelectedKeys(newSelectedKeys);
  // };

  // Utility function to find a node by key
  // const findNodeByKey = (nodes, key) => {
  //   for (let node of nodes) {
  //     if (node.key === key) return node;
  //     if (node.children) {
  //       const found = findNodeByKey(node.children, key);
  //       if (found) return found;
  //     }
  //   }
  //   return null;
  // };

  //Handle Select All checkbox
  const handleSelectAll = (e) => {
    if (!vehAssignedUpdated) {
      setVehAssignedUpdated(true);
    }
    //setIsSelectAllLoading(true);
    const { checked } = e.target;
    let updatedKeys = {};
    if (checked) {
      //   setSelectedKeys((prev) => {
      //     let temp = { ...prev };
      //     for (let group of groups) {
      //       if (!temp.hasOwnProperty(`group_${group.group_id}`)) {
      //         temp[`group_${group.group_id}`] = { checked: true };
      //       }
      //     }

      //     for (let vehicle of vehicles) {
      //       if (!temp.hasOwnProperty(`vehicle_${vehicle.veh_id}`)) {
      //         temp[`vehicle_${vehicle.veh_id}`] = { checked: true };
      //       }
      //     }

      //     return temp;
      //   });
      // Select all groups
      groups.forEach((group) => {
        updatedKeys[`group_${group.group_id}`] = { checked: true };
      });

      // Select all vehicles
      vehicles.forEach((vehicle) => {
        updatedKeys[`vehicle_${vehicle.veh_id}`] = { checked: true };
      });

      setVehiclesAssigned([...vehicles]);
    } else {
      setVehiclesAssigned([]);
      if (roleId == 2) {
        checkMultiVehiclesUserAssigned(vehicles);
      }
      //setSelectedKeys({});
    }
    setSelectedKeys(updatedKeys);
    //setIsSelectAllLoading(false);
  };

  // Handle removing a vehicle from the assigned list
  const handleRemoveVehicle = (veh_id, group_id) => {
    if (!vehAssignedUpdated) {
      setVehAssignedUpdated(true);
    }
    // Remove from assigned vehicles
    setVehiclesAssigned((prev) => prev.filter((v) => v.veh_id !== veh_id));

    if (roleId == 2) {
      checkUserAssigned(veh_id);
    }

    setSelectedKeys((prevSelectedKeys) => {
      // Step 1: Remove the vehicle from selectedKeys
      const updatedKeys = { ...prevSelectedKeys };
      delete updatedKeys[`vehicle_${veh_id}`];

      // Step 2: Check if the group still has any selected vehicles
      // const groupHasOtherSelectedVehicles = Object.keys(updatedKeys).some(
      //   (key) =>
      //     key.startsWith("vehicle_") &&
      //     vehicles.find(
      //       (v) =>
      //         v.veh_id === key.replace("vehicle_", "") &&
      //         v.group_id === group_id
      //     )
      // );

      // let groupHasOtherChildren = false;
      // for (let key in updatedKeys) {
      //   if (key.startsWith("vehicle_")) {
      //     groupHasOtherChildren = vehicles.find(
      //       (v) =>
      //         v.veh_id === key.replace("vehicle_", "") &&
      //         v.group_id === group_id
      //     );
      //   }

      //   if (key.startsWith("group_")) {
      //     groupHasOtherChildren = groups
      //       ?.find((g) => g.group_id === group_id)
      //       ?.groupNodes?.includes(key.replace("group_", ""));
      //   }

      //   if (groupHasOtherChildren) {
      //     break;
      //   }
      // }

      // Step 3: If the group has no other selected vehicles, uncheck the group
      // if (!groupHasOtherChildren) {
      //   delete updatedKeys[`group_${group_id}`];
      // }

      //Remove partialChecked property
      // if (!isObjEmpty(updatedKeys)) {
      //   for (let key in updatedKeys) {
      //     if (
      //       updatedKeys[key].hasOwnProperty("partialChecked") &&
      //       updatedKeys[key].partialChecked
      //     ) {
      //       delete updatedKeys[key];
      //     }
      //   }
      // }

      return updatedKeys;
    });
  };

  const nodeTemplate = (node) => {
    const isChecked = selectedKeys[node.key]?.checked; // Check if the group is selected

    return (
      <div>
        <span style={{ fontSize: 14, color: colorTextNew }}>{node.label}</span>
        {isChecked && node.children && (
          <div
            style={{
              fontSize: "12px",
              color: "gray",
              //marginLeft: "20px",
            }}
          >
            {t("future_veh_auto_assigned_msg")}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {noUsersAssignedWarningMsg && (
        <InfoPopup
          message={noUsersAssignedWarningMsg?.msg}
          icon={{
            className: "fi fi-rr-triangle-warning",
            color: colorYellowNew,
          }}
          showPopup={noUsersAssignedWarningMsg}
          onClose={() => setNoUsersAssignedWarningMsg("")}
          listMsg={noUsersAssignedWarningMsg?.listMsg}
          //setShowPopup={setShowNoUserAssignedWarning}
        />
      )}
      <Container>
        {role?.value == 4 || role?.value == 1 ? (
          <Row>
            <Col>
              <Card>
                <CardBody
                  style={{
                    height: `${window.innerHeight - 210}px`,
                  }}
                  className="d-flex justify-content-center align-items-center"
                >
                  <p style={{ fontSize: 18, fontWeight: 500 }}>
                    {role.value == 4
                      ? t("tech_have_access_to_all_vehicles")
                      : t("admins_have_access_to_all_vehicles")}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={6}>
              <Card>
                <CardBody
                  style={{
                    height: `${window.innerHeight - 200}px`,
                  }}
                >
                  <Row>
                    <Col>
                      {" "}
                      <h5 className="mb-2" id="vehicles-to-be-added-text">
                        {t("vehicles_to_be_added_label")}
                      </h5>
                      <FormItem disabled={!status}>
                        <input
                          disabled={
                            !status || (!vehicles?.length && !groups?.length)
                          }
                          type="text"
                          name="vehicle-filter-search"
                          id="vehicle-filter-search"
                          value={filterValue}
                          onChange={(e) => setFilterValue(e.target.value)}
                          style={formInputStyles}
                          placeholder={`${t("group")} / ${t("asset_name")}`}
                        />
                      </FormItem>
                      <FormItem
                        className="d-flex justify-content-start px-2"
                        disabled={!status || isFiltering}
                      >
                        <CheckboxInputComponent
                          checked={isSelectAllChecked}
                          //setChecked={setSelectAll}
                          id={"select-all-checkbox"}
                          label={t("select_all")}
                          onChange={handleSelectAll}
                          disabled={
                            !status ||
                            isFiltering ||
                            (!vehicles?.length && !groups?.length)
                          }
                        />
                      </FormItem>
                      {/* <VehicleListContainer> */}
                      <Tree
                        value={treeData}
                        selectionMode="checkbox"
                        selectionKeys={selectedKeys}
                        onSelectionChange={handleSelectionChange}
                        //filter
                        nodeTemplate={nodeTemplate}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                        disabled={!status}
                        propagateSelectionUp={false}
                        //loading={isSelectAllLoading}
                        //filterValue={filterValue}
                        //onFilterValueChange={(e) => setFilterValue(e.value)}
                      />
                      {/* </VehicleListContainer> */}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <CardBody
                  style={{
                    height: `${window.innerHeight - 200}px`,
                  }}
                >
                  <Row>
                    <Col>
                      <h5 className="mb-2">{t("assigned_vehicles")}</h5>

                      <AssignedVehiclesContainer>
                        {vehicles?.length === 0 &&
                          groups?.length === 0 &&
                          vehiclesAssigned?.length === 0 && (
                            <div className="d-flex justify-content-center align-items-center h-100">
                              <p>{t("no_veh_to_be_assigned")}</p>
                            </div>
                          )}
                        {vehiclesAssigned.map((veh) => {
                          return (
                            <div
                              key={veh.veh_id}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div className="d-flex gap-3 my-2">
                                <VehicleImg
                                  src={
                                    veh.veh_img
                                      ? `${s3Url}/org_${orgId}/vehicle_${veh.veh_id}/${veh.veh_img}`
                                      : veh.asset_type === 0
                                      ? defaultVehicleIcon
                                      : defaultEquipmentIcon
                                  }
                                  alt="vehicle-img"
                                  // style={{ width: "40px", height: "40px" }}
                                  className="rounded-circle"
                                />
                                <p>{veh.label}</p>
                              </div>
                              {status && (
                                <ActionIconContainer
                                  onClick={() =>
                                    handleRemoveVehicle(
                                      veh.veh_id,
                                      veh.group_id
                                    )
                                  }
                                >
                                  <i className="fi fi-rs-cross-small" />
                                </ActionIconContainer>
                              )}
                            </div>
                          );
                        })}
                      </AssignedVehiclesContainer>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default VehiclesAssignedFormNew;

//styles
export const AssignedVehiclesContainer = styled.div`
  height: ${window.innerHeight - 286}px;
  background: #f9f9f9;
  overflow: auto;
  ${scrollbarStyles};
  border: 1px solid lightgray;
  border-radius: 5px;
  padding: 0.5rem;
  //background: lightgray;
`;
